import React, { Component } from "react";
import PropTypes from "prop-types";
import { withLDConsumer } from "launchdarkly-react-client-sdk";
import utils from "@utils";
import { PaymentService } from "@services";
import { REFUND_REASONS_TYPES } from "@constants";
import { Loader } from "@components/common";
import { Modal } from "../../../../../components";
import "./RefundModal.scss";

class RefundModal extends Component {
	constructor(props) {
		super(props);
		this.state = {
			loading: false,
			refunding: false,
			balance: 0,
			chargeRefunds: 0,
			errors: {
				amount: "",
				reason: "",
				description: "",
			},
			expandOptions: false,
			selectedReason: null,
		};

		this.amountInput = React.createRef();
		this.descriptionInput = React.createRef();
	}

	componentDidUpdate(prevProps) {
		const { show } = this.props;
		if (show && !prevProps.show) {
			this.setState({ loading: true });
			this.getChargeRefunds();
		}
	}

	beforeClose = () => {
		const { closeModal } = this.props;
		this.setState({
			errors: {
				amount: "",
				reason: "",
				description: "",
			},
			refunding: false,
			selectedReason: null,
			expandOptions: false,
		});

		closeModal();
	};

	getChargeRefunds = () => {
		const { payment } = this.props;
		const { lang } = global;
		PaymentService.getChargeRefunds(payment.StripeTransactionId)
			.then(response => {
				const balance = utils.decimalFormatter(
					response.data.amount - response.data.amount_refunded,
				);
				const chargeRefunds = utils.decimalFormatter(response.data.amount_refunded);
				this.setState({
					chargeRefunds,
					balance,
					loading: false,
				});
			})
			.catch(() => {
				this.setState({ loading: false });
				this.beforeClose();
				utils.notify({
					type: "error",
					message: lang.t("Error retrieving refunded amount"),
				});
			});
	};

	refund = () => {
		const { selectedReason, balance } = this.state;
		const { payment } = this.props;
		const { lang } = global;

		let refundAmount = this.amountInput.current.value;

		if (!refundAmount || refundAmount.trim() === "") {
			refundAmount = balance;
		}

		refundAmount *= 100;

		PaymentService.createRefund({
			paymentId: payment.StripeTransactionId,
			amount: refundAmount,
			reason: selectedReason.value,
			description: this.descriptionInput.current.value,
		})
			.then(() => {
				utils.notify({
					type: "success",
					message: lang.t("Refund successful"),
				});
			})
			.catch(({ response }) => {
				if (response.status >= 500) {
					utils.notify({
						type: "error",
						message: lang.t("Refund failed"),
					});
				} else if (response.status >= 400) {
					utils.notify({
						type: "error",
						message: response.data.Message,
					});
				}
			})
			.finally(() => {
				this.setState({ refunding: false });
				this.beforeClose();
			});
	};

	amountValidate = () => {
		const { lang } = global;
		const { orgBalance, payment } = this.props;

		const amount = this.amountInput.current.value;
		const regex = /^\d*\.?\d+$/;

		if (!amount || amount.trim() === "") {
			return "";
		}
		if (!regex.test(amount)) {
			return lang.t("Invalid amount value");
		}
		if (amount <= 0) {
			return lang.t("Amount must be greater than zero");
		}
		if (amount > orgBalance) {
			return lang.t("Amount must be equal to or less than the organization balance");
		}
		if (amount > payment.Amount) {
			return lang.t("Amount must be equal to or less than the payment balance");
		}

		return "";
	};

	descriptionValidate = () => {
		const { lang } = global;

		const description = this.descriptionInput.current.value;
		if (!description || description.trim() === "") {
			return lang.t("Description can not be empty");
		}

		return "";
	};

	reasonValidate = () => {
		const { lang } = global;
		const { selectedReason } = this.state;

		const reason = selectedReason;
		if (!selectedReason || !REFUND_REASONS_TYPES.some(r => r.value.includes(reason.value))) {
			return lang.t("Please select a valid reason");
		}

		return "";
	};

	formValidation = () => {
		this.setState({ refunding: true });
		const amountError = this.amountValidate();
		const reasonError = this.reasonValidate();
		const descriptionError = this.descriptionValidate();

		this.setState(() => ({
			errors: {
				reason: reasonError,
				amount: amountError,
				description: descriptionError,
			},
		}));

		if (amountError === "" && reasonError === "" && descriptionError === "") {
			this.refund();
		} else {
			this.setState({ refunding: false });
		}
	};

	toggleDropdown = () => {
		this.setState(prevState => ({
			expandOptions: !prevState.expandOptions,
		}));
	};

	handleReasonSelect = reason => {
		this.setState({
			selectedReason: reason,
		});
		this.toggleDropdown();
	};

	render() {
		const { lang } = global;
		const { show, payment } = this.props;
		const {
			loading,
			refunding,
			errors,
			expandOptions,
			selectedReason,
			chargeRefunds,
			balance,
		} = this.state;
		return (
			<Modal
				id="organization-settings-modal"
				dialogClassName="organization-settings-dialog"
				show={show}
				size="mg"
				onHide={this.beforeClose}
				closeModal={this.beforeClose}
			>
				<Modal.Header
					closeButton
					className="hidden-print"
				>
					<Modal.Title>{lang.t("Issue Refund")}</Modal.Title>
				</Modal.Header>
				<Modal.Body className="container-fluid">
					<div style={{ marginLeft: "15px", marginRight: "15px" }}>
						<div className="table-overflow">
							<div
								key={`orguser-${payment.Id}`}
								className="row v-align-sm"
							>
								<div className="col-sm-2">
									{utils
										.parseUTCDateToTimeZone(payment.CreatedDateUTC, "UTC")
										.format("MM/DD/YYYY")}
								</div>
								<div className="col-sm-3">
									<i className="rkf rkf-clock text-gray" />
									{utils
										.parseUTCDateToTimeZone(payment.CreatedDateUTC, "UTC")
										.format("h:mm A")}
								</div>
								<div
									className={`col-sm-3 ${payment.Amount >= 0 ? "text-success" : "text-danger"}`}
								>
									{payment.Amount >= 0
										? `+ ${utils.currencyFormatter(payment.Amount)}`
										: `- ${utils.currencyFormatter(Math.abs(payment.Amount))}`}
								</div>
								<div className="col-sm-4">
									<span className="multiline">{payment.StripeTransactionId}</span>
								</div>
							</div>
						</div>
						<div className="row v-align-sm">
							<div className="col-sm-5">{lang.t("Total Refunded Amount")}</div>
							<div className="col-sm-4 text-danger">
								- {utils.currencyFormatter(chargeRefunds)}
							</div>
						</div>
						<br />
					</div>
					<div className="row form-group">
						<div className="col-xs-12 col-sm-4">
							<label htmlFor="newAmount">{`${lang.t("Refund Amount")}:`}</label>
						</div>
						<div className="col-xs-12 col-sm-8">
							<input
								name="newAmount"
								id="newAmount"
								ref={this.amountInput}
								type="text"
								className="form-control"
								placeholder={utils.currencyFormatter(balance)}
							/>
							<div className="text-danger">{errors.amount}</div>
						</div>
					</div>
					<div className="row form-group">
						<div className="col-xs-12 col-sm-4">
							<label htmlFor="reason">{`${lang.t("Reason")}:`}</label>
						</div>
						<div className="col-xs-12 col-sm-8">
							<div
								className={`'dropdown btn-group btn-group-link' ${expandOptions ? "open" : ""}`}
							>
								<button
									type="button"
									className="btn btn-link btn-block dropdown-toggle"
									onClick={this.toggleDropdown}
								>
									{selectedReason ? selectedReason.label : lang.t("Reason")}
									&nbsp;
									<span className="caret" />
								</button>
								<ul
									role="menu"
									className="dropdown-menu"
								>
									<For
										each="reason"
										of={REFUND_REASONS_TYPES}
									>
										<li
											key={reason.value}
											role="presentation"
										>
											<button
												type="button"
												className="btn btn-link btn-block text-left"
												onClick={() => this.handleReasonSelect(reason)}
											>
												{reason.label}
											</button>
										</li>
									</For>
								</ul>
							</div>
							<div className="text-danger">{errors.reason}</div>
						</div>
					</div>
					<div className="row form-group">
						<div className="col-xs-12 col-sm-4">
							<label htmlFor="description">{`${lang.t("Description")}:`}</label>
						</div>
						<div className="col-xs-12 col-sm-8">
							<input
								name="description"
								id="description"
								ref={this.descriptionInput}
								type="text"
								className="form-control"
								placeholder={lang.t("Please add a description for this refund")}
							/>
							<div className="text-danger">{errors.description}</div>
						</div>
					</div>
				</Modal.Body>
				<Modal.Footer>
					<button
						id="button-print-table"
						className="btn cancel-btn hidden-print"
						type="button"
						onClick={this.beforeClose}
					>
						{lang.t("No, Cancel")}
					</button>
					<button
						id="button-print-table"
						className="btn btn-primary hidden-print"
						type="button"
						onClick={this.formValidation}
					>
						{refunding ? lang.t("Refunding") : lang.t("Yes, refund amount")}
					</button>
				</Modal.Footer>
				<Loader
					active={loading}
					size="small"
				/>
			</Modal>
		);
	}
}

RefundModal.propTypes = {
	show: PropTypes.bool,
	closeModal: PropTypes.func,
	payment: PropTypes.object,
	orgBalance: PropTypes.number,
};

RefundModal.defaultProps = {
	show: false,
	closeModal: () => {},
	payment: {},
	orgBalance: null,
};

export default withLDConsumer()(RefundModal);
